import { useQuery } from '@tanstack/react-query';
import { create } from 'zustand';

import { useCurrenciesStore } from '@/entities/currency';
import { serializePayment } from '@/entities/payment/lib/serialize';

import {
  VabsFindPaymentsInput,
  VabsMyPaymentsQuery,
  VabsPaymentStatus,
  requestsClient,
} from '@/shared/api';
import { useEffect } from 'react';

type NeedActionPayments = VabsMyPaymentsQuery['vabsMyPayments']['nodes'];
const needActionPaymentsQuery = async () => {
  const input: VabsFindPaymentsInput = {
    statuses: [VabsPaymentStatus.NeedAction],
  };

  const { vabsMyPayments } = await requestsClient.vabsMyPayments({ input });

  return vabsMyPayments.nodes;
};

export const useComplianceRequestsStore = () => {
  const currencies = useCurrenciesStore((state) => state.currenciesById);
  const {
    shouldAutoOpenComplianceModal,
    setShouldAutoOpenComplianceModal,
    setRequestsCount,
  } = useComplianceAutoOpenStore();

  const {
    data: needActionPayments = [],
    isPending: isNeedActionPaymentsPending,
  } = useQuery({
    queryKey: ['need-action-payments'],
    queryFn: needActionPaymentsQuery,
    refetchOnMount: 'always',
    select: (payments: NeedActionPayments) =>
      payments.map((payment) => serializePayment(payment, currencies)),
  });

  useEffect(() => {
    if (needActionPayments?.length) {
      setRequestsCount(needActionPayments?.length)
    } else {
      setRequestsCount(0)
    }
  }, [needActionPayments, setRequestsCount])

  return {
    needActionPayments,
    isNeedActionPaymentsPending,
    shouldAutoOpenComplianceModal,
    setShouldAutoOpenComplianceModal,
  };
};

interface ComplianceAutoOpenStore {
  shouldAutoOpenComplianceModal: boolean;
  requestsCount: number;
  isInPayment: boolean; //supresses opening of modal for the duration of payment flow
  setShouldAutoOpenComplianceModal: (value: boolean) => void;
  setRequestsCount: (value: number) => void;
  setIsInPayment: (value: boolean) => void;
}

export const useComplianceAutoOpenStore = create<ComplianceAutoOpenStore>(
  (set) => ({
    shouldAutoOpenComplianceModal: true,
    isInPayment: false,
    requestsCount: 0,
    setShouldAutoOpenComplianceModal: (value) => {
      set({ shouldAutoOpenComplianceModal: value });
    },
      setIsInPayment: (value) => {
      set({ isInPayment: value });
    },
     setRequestsCount: (value) => {
      set({ requestsCount: value });
    },
  }),
);
